/**
 * The chat drawer's width drag.
 *
 * The pointer owns the width while it is down: each move writes a preview
 * custom property straight to the wrap, so the transcript does not re-render
 * per sample, and React's committed width is set once on release.
 *
 * Pointer capture does not guarantee an up or cancel event when the captured
 * strip unmounts, so closing and unmounting both route through one idempotent
 * cancel — the stage-resize bracket must be balanced exactly once however the
 * drag ends.
 */

import type { PointerEvent as ReactPointerEvent, RefObject } from 'react';
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

import { clearChatWidthPreview, writeChatWidthPreview } from '../panelGeometry';
import { beginStageResize, endStageResize } from '../stageMotion';
import { usePointerDrag } from '@/hooks/usePointerDrag';

import { clampChatWidth } from './chatWidth';

/** Where a resize drag began, and the drawer's width then. */
interface ChatDrag {
    startX: number;
    startWidth: number;
}

export function useChatResize({ wrapRef, open: isOpen, width, setWidth, onResizeChange }: {
    wrapRef: RefObject<HTMLDivElement | null>;
    open: boolean;
    width: number;
    setWidth: (width: number) => void;
    onResizeChange?: (isResizing: boolean) => void;
}): { onPointerDown: (event: ReactPointerEvent<HTMLDivElement>) => void } {
    const dragRef = useRef<ChatDrag | null>(null);

    const applyPreview = (px: number): void => {
        const el = wrapRef.current;
        if (el) {
            writeChatWidthPreview(el, px);
        }
    };

    const cancelResize = useCallback((): void => {
        if (dragRef.current === null) {
            return;
        }
        dragRef.current = null;
        clearChatWidthPreview(wrapRef.current);
        endStageResize();
        onResizeChange?.(false);
    }, [wrapRef, onResizeChange]);

    useLayoutEffect((): void => {
        if (!isOpen) {
            cancelResize();
        }
    }, [isOpen, cancelResize]);
    useEffect(() => (): void => cancelResize(), [cancelResize]);

    // A changed committed width has reached the DOM. Drop the preview before
    // paint so future drags always start from React-owned geometry.
    useLayoutEffect((): void => {
        if (dragRef.current === null) {
            clearChatWidthPreview(wrapRef.current);
        }
    }, [width, wrapRef]);

    const getWidthAt = (drag: ChatDrag, clientX: number): number => (
        clampChatWidth(drag.startWidth + (clientX - drag.startX))
    );

    return usePointerDrag({
        onStart: (event: ReactPointerEvent<HTMLDivElement>): void => {
            dragRef.current = { startX: event.clientX, startWidth: width };
            clearChatWidthPreview(wrapRef.current);
            wrapRef.current?.setAttribute('data-resizing', '');
            // This drag resizes the stage exactly as a panel splitter does, so it
            // owes the same bracket: the Objects gallery parks its row packing while
            // the stage is in motion (useGalleryWidth), and without this it re-packs
            // on every pointer move of a drag that can sweep 360px.
            beginStageResize();
            onResizeChange?.(true);
        },
        onMove: (event: PointerEvent): void => {
            const drag = dragRef.current;
            if (drag) {
                applyPreview(getWidthAt(drag, event.clientX));
            }
        },
        onCancel: cancelResize,
        onEnd: (event: PointerEvent): void => {
            const drag = dragRef.current;
            if (!drag) {
                return;
            }
            dragRef.current = null;
            const nextPx = getWidthAt(drag, event.clientX);
            applyPreview(nextPx);
            setWidth(nextPx);
            if (nextPx === width) {
                clearChatWidthPreview(wrapRef.current);
            }
            endStageResize();
            onResizeChange?.(false);
        },
    });
}
